/**
 * 動画の解像度バリアント（画質）を利用可能にするための共通処理
 *
 * 未生成の画質は変換を依頼し、完了までポーリングする。
 * 画質選択UI（useVideoQuality）とダウンロードボタンの双方から使う。
 */
import { toast } from 'sonner'

import { getVideoVariantStatus, requestVideoVariant } from '@/api/api'

const POLL_INTERVAL = 2000

type PrepareOptions = {
  /** 変換の進捗率（0–100）通知 */
  onProgress?: (progress: number) => void
  /** 中断用シグナル（abort でポーリングを打ち切り false を返す） */
  signal?: AbortSignal
}

const wait = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    function onAbort() {
      clearTimeout(timer)
      resolve()
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })

/**
 * 指定画質を利用可能な状態にする。
 * 生成済みなら即座に true を返し、未生成なら変換完了まで待つ。
 *
 * @returns 利用可能になったら true（失敗・中断なら false）
 */
export const prepareVideoVariant = async (
  episodeId: number,
  quality: string,
  { onProgress, signal }: PrepareOptions = {},
): Promise<boolean> => {
  if (quality === 'original') return true

  try {
    const res = await requestVideoVariant(episodeId, quality)
    if (res.status === 'ready') return true
    if (res.status === 'failed') {
      toast.error(res.error_message ?? '画質の変換に失敗しました')
      return false
    }
    onProgress?.(res.progress)
  } catch {
    toast.error('変換の依頼に失敗しました')
    return false
  }

  while (!signal?.aborted) {
    await wait(POLL_INTERVAL, signal)
    if (signal?.aborted) return false

    try {
      const status = await getVideoVariantStatus(episodeId, quality)
      if (status.status === 'ready') return true
      if (status.status === 'failed' || status.status === 'none') {
        toast.error(status.error_message ?? '画質の変換に失敗しました')
        return false
      }
      onProgress?.(status.progress)
    } catch {
      toast.error('変換状況の取得に失敗しました')
      return false
    }
  }

  return false
}
